import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ExternalLink, ArrowLeft, ChevronRight, CalendarDays, Phone, Instagram, Facebook } from 'lucide-react';
import SEO from './SEO';
import CategoryNav from './CategoryNav';
import { useManagedCollection } from '../admin/useManagedCollection';
import { EVENTS, eventSlug, parseLocal, isEventUpcoming, type EventItem } from './Events';

const BASE_URL = 'https://dallasdesiscoop.com';

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const CATEGORY_COLOR: Record<NonNullable<EventItem["category"]>, string> = {
  Concert: "#8B5CF6",
  Festival: "#f97316",
  Expo: "#2563eb",
  "Theater & Plays": "#ec4899",
};

function formatRange(e: EventItem): string {
  if (!e.date) return "";
  const s = parseLocal(e.date);
  if (e.endDate) {
    const en = parseLocal(e.endDate);
    return `${MONTHS[s.getMonth()]} ${s.getDate()}–${en.getDate()}, ${s.getFullYear()}`;
  }
  return `${MONTHS[s.getMonth()]} ${s.getDate()}, ${s.getFullYear()}`;
}

export default function EventDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('events_featured', EVENTS, (e) => eventSlug(e as EventItem));

  const entry = mc.items.find((e: any) => eventSlug(e as EventItem) === slug) as EventItem | undefined;

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Event Not Found | Dallas Desi Scoop"
          description="This event listing could not be found."
          canonical="/events"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Event not found.</p>
        <Link to="/events" className="text-[#082f6d] font-semibold hover:underline">
          ← Back to Events
        </Link>
      </div>
    );
  }

  const upcoming = isEventUpcoming(entry);
  const color = entry.category ? CATEGORY_COLOR[entry.category] : "#8B5CF6";
  const city = entry.city || 'DFW';
  const title = `${entry.name} in ${city}, TX | Dallas Desi Scoop`;
  const description = `${entry.name}${entry.date ? ` on ${formatRange(entry)}` : ''}${entry.venue ? ` at ${entry.venue}` : ''} in ${city}, TX. Find date, time, venue, tickets, and contact info.`;
  const canonicalPath = `/events/${slug}`;

  const mapsUrl = entry.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(entry.address)}`
    : '';

  const jsonLd = [
    {
      '@type': 'Event',
      name: entry.name,
      image: entry.image ? [entry.image] : undefined,
      startDate: entry.date,
      endDate: entry.endDate || entry.date,
      eventStatus: upcoming ? 'https://schema.org/EventScheduled' : 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      location: entry.address
        ? {
            '@type': 'Place',
            name: entry.venue || entry.name,
            address: entry.address,
          }
        : undefined,
      offers: entry.link
        ? { '@type': 'Offer', url: entry.link, availability: 'https://schema.org/InStock' }
        : undefined,
      url: `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Events', item: `${BASE_URL}/events` },
        { '@type': 'ListItem', position: 3, name: entry.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={description}
        canonical={canonicalPath}
        ogImage={entry.image}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '300px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.78) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.9) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/events"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Events
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium truncate max-w-[240px]">{entry.name}</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 mb-3"
          >
            {entry.category && (
              <span
                className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white"
                style={{ background: upcoming ? color : '#6b7280' }}
              >
                {upcoming ? entry.category : 'Past Event'}
              </span>
            )}
            {entry.isFree && (
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-green-600 text-white">
                Free Event
              </span>
            )}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {entry.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            {entry.date && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <CalendarDays className="w-3.5 h-3.5 text-[#51ba4f]" />
                {formatRange(entry)}{entry.time ? ` · ${entry.time}` : ''}
              </span>
            )}
            {entry.venue && (
              <>
                <span className="text-white/40">·</span>
                <span className="flex items-center gap-1 text-white/85 text-[13px]">
                  <MapPin className="w-3.5 h-3.5 text-[#f59e0b]" />
                  {entry.venue}{entry.city ? `, ${entry.city}, TX` : ''}
                </span>
              </>
            )}
          </motion.div>
        </div>
      </section>

      <CategoryNav />

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About this event</h2>
          {entry.description ? (
            <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">{entry.description}</p>
          ) : (
            <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
              {entry.name}
              {entry.date ? `, happening ${upcoming ? '' : 'previously '}on ${formatRange(entry)}` : ''}
              {entry.time ? ` at ${entry.time}` : ''}
              {entry.venue ? ` at ${entry.venue}` : ''}
              {entry.city ? ` in ${entry.city}, TX` : ''}. A part of the Dallas–Fort Worth Desi community
              events calendar on DallasDesiScoop.
            </p>
          )}

          {entry.sessions && entry.sessions.length > 0 && (
            <div className="mt-6">
              <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-2">Sessions</h3>
              <ul className="space-y-1.5">
                {entry.sessions.map((s, i) => (
                  <li key={i} className="flex items-center gap-2 text-[13px] text-gray-700">
                    <CalendarDays className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {entry.featuredBy && (
            <div className="mt-6">
              <span className="text-[12px] font-semibold text-pink-600 bg-pink-50 px-2 py-1 rounded-full">
                Featured by {entry.featuredBy}
              </span>
            </div>
          )}
        </div>

        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Details & Tickets
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            {entry.date && (
              <div className="flex items-start gap-2">
                <CalendarDays className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{formatRange(entry)}{entry.time ? ` · ${entry.time}` : ''}</span>
              </div>
            )}
            {(entry.venue || entry.address) && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>
                  {entry.venue}
                  {entry.venue && entry.address ? ' · ' : ''}
                  {entry.address || (entry.city ? `${entry.city}, TX` : '')}
                </span>
              </div>
            )}
            {entry.price && (
              <div className="flex items-start gap-2">
                <span className="text-[13px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                  Entry Fee: {entry.price}
                </span>
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-col gap-2">
            {entry.link && (
              <a
                href={entry.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 bg-[#f59e0b] hover:bg-[#d97706] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Get Tickets
              </a>
            )}
            {entry.websiteLink && (
              <a
                href={entry.websiteLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Website
              </a>
            )}
            {!!mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 bg-white border border-orange-200 text-orange-600 hover:bg-orange-50 text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
              >
                <MapPin className="w-4 h-4" />
                View Location
              </a>
            )}
            {entry.contact && !entry.contactUrl && (
              <a
                href={`tel:${entry.contact.replace(/[^\d+]/g, '')}`}
                className="flex items-center justify-center gap-1.5 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call
              </a>
            )}
            {entry.contactUrl && (
              <a
                href={entry.contactUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call
              </a>
            )}
            {entry.contacts && entry.contacts.map((num, i) => (
              <a
                key={i}
                href={`tel:${num.replace(/[^\d+]/g, '')}`}
                className="flex items-center justify-center gap-1.5 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call
              </a>
            ))}
            {entry.instagramUrl && (
              <a
                href={"https://" + String(entry.instagramUrl).replace(/^https?:\/\//i, "")}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 bg-gradient-to-br from-pink-500 to-orange-400 hover:opacity-90 text-white text-[13px] font-semibold py-2.5 rounded-lg transition-opacity"
              >
                <Instagram className="w-4 h-4" />
                Instagram
              </a>
            )}
            {entry.facebookUrl && (
              <a
                href={entry.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 bg-[#1877F2] hover:bg-[#166fe0] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
              >
                <Facebook className="w-4 h-4" />
                Facebook
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
